import { isPlatformBrowser, NgClass } from '@angular/common';
import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import { Component, DestroyRef, DOCUMENT, inject, input, PLATFORM_ID, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import { SearchInputComponent } from 'components/molecules/search-input/search-input.component';
import type { MenuItem, SidebarSetting } from 'components/organisms/sidebar/entities/types';
import { MenuItemComponent } from 'components/organisms/sidebar/partial/menu-item/menu-item.component';
import { MenuItemsListComponent } from 'components/organisms/sidebar/partial/menu-items-list/menu-items-list.component';
import { SidebarService } from 'components/organisms/sidebar/sidebar.service';
import { StickyService } from 'src/services/sticky/sticky.service';
import { WindowResizeService } from 'src/services/window-resize/window-resize.service';
import { INNER_WIDTH_LAPTOP } from 'src/shared/constants/breakpoints/breakpoints';

@Component({
    selector: 'website-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    imports: [SearchInputComponent, NgClass, TranslatePipe, MenuItemsListComponent, MenuItemComponent],
})
export class SidebarComponent extends AbstractWebsiteComponent implements OnInit, AfterViewInit {
    public readonly COMPONENT_NAME = 'sidebar';

    public readonly sidebarService = inject(SidebarService);
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    public readonly stickyService = inject(StickyService);
    private readonly windowResizeService = inject(WindowResizeService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    readonly sidebarSetting = input.required<SidebarSetting>();
    private readonly scrollContent = viewChild.required<ElementRef>('scrollContent');

    public isTablet: boolean;
    public menuItem: MenuItem;

    ngOnInit() {
        this.menuItem = {
            title: this.translateService.instant('Guide overview'),
            url: '',
            isAnchor: false,
        };

        if (isPlatformBrowser(this.platformId)) {
            this.initIsTablet();
            this.setupResizeListener();
            this.setupScrollListener();
        }
    }

    ngAfterViewInit() {
        this.setScrollContentHeight();
    }

    private setupScrollListener(): void {
        fromEvent(window, 'scroll', { passive: true })
            .pipe(throttleTime(50), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.checkSidebarHeaderIntersection();
            });
    }

    private checkSidebarHeaderIntersection(): void {
        const headerShadowElement = this.document.querySelector<HTMLDivElement>('.js-header__shadow');

        if (this.isTablet) {
            const sidebarParentContainer = this.document.getElementById('sidebar-menu');
            const SCROLL_TRASH_HOLD = 50;

            sidebarParentContainer.classList.toggle('border-active', window.pageYOffset > SCROLL_TRASH_HOLD);
            headerShadowElement.style.display = 'none';
        } else {
            headerShadowElement.style.display = 'block';
        }
    }

    private setupResizeListener(): void {
        this.windowResizeService.resize$.pipe(throttleTime(100), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.sidebarService.removeOverflow();
            this.initIsTablet();
            this.setScrollContentHeight();
        });
    }

    private setScrollContentHeight(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const scrollContentEl = this.scrollContent().nativeElement;
        const viewportHeight = window.innerHeight;
        const targetHeight = Math.floor(viewportHeight * 0.85);

        scrollContentEl.style.height = `${targetHeight}px`;

        if (this.isTablet) {
            scrollContentEl.style.removeProperty('height');
        }
    }

    private initIsTablet() {
        this.isTablet = window.innerWidth <= INNER_WIDTH_LAPTOP;
    }
}
